"use client";

import { useCallback, useState } from "react";
import type { IdentifiedTopic } from "@/generated/fastapi";
import { sortIdentifiedTopicsByConfidence } from "../../utils";

export type UpdateTopicInput =
  | Partial<IdentifiedTopic>
  | ((topic: IdentifiedTopic) => Partial<IdentifiedTopic>);

export interface IdentifiedTopicsState {
  identifiedTopics: IdentifiedTopic[] | null;
  setTopics: (topics: IdentifiedTopic[] | null) => void;
  removeTopic: (topicId: string) => void;
  clearTopics: () => void;
  updateTopic: (topicId: string, update: UpdateTopicInput) => void;
  hasUnconfirmedTopics: () => boolean;
}

export default function useIdentifiedTopicsState(): IdentifiedTopicsState {
  const [identifiedTopics, setIdentifiedTopics] = useState<
    IdentifiedTopic[] | null
  >(null);

  const setTopics = useCallback((topics: IdentifiedTopic[] | null) => {
    if (topics) {
      setIdentifiedTopics(sortIdentifiedTopicsByConfidence(topics));
    } else {
      setIdentifiedTopics(topics);
    }
  }, []);

  const removeTopic = useCallback(
    (topicId: string) => {
      if (!identifiedTopics) return;
      const updatedTopics = identifiedTopics.filter(
        (topic) => topic.id !== topicId,
      );
      setIdentifiedTopics(updatedTopics);
    },
    [identifiedTopics],
  );

  const clearTopics = useCallback(() => {
    setIdentifiedTopics([]);
  }, []);

  const updateTopic = useCallback(
    (topicId: string, update: UpdateTopicInput) => {
      if (!identifiedTopics) return;

      const updatedTopics = identifiedTopics.map((topic) => {
        if (topic.id !== topicId) return topic;

        const partialUpdate =
          typeof update === "function" ? update(topic) : update;

        return { ...topic, ...partialUpdate };
      });

      setIdentifiedTopics(updatedTopics);
    },
    [identifiedTopics],
  );

  const hasUnconfirmedTopics = useCallback((): boolean => {
    if (!identifiedTopics) return false;
    return identifiedTopics.some((topic) => !topic.confirmed_at);
  }, [identifiedTopics]);

  return {
    identifiedTopics,
    setTopics,
    removeTopic,
    clearTopics,
    updateTopic,
    hasUnconfirmedTopics,
  };
}
